"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.writeUniqueByUser = exports.writeUniqueByObject = exports.write = exports.requireClient = exports.remove = exports.readUsers = exports.readObjects = exports.listObjects = exports.check = exports.batchWrite = void 0;
const sdk_1 = require("@openfga/sdk");
const arrays_1 = require("./arrays");
const errors_1 = require("./errors");
let client;
const requireClient = () => {
    if (!client || client.storeId !== process.env.AUTHZ_STORE_ID) {
        client = new sdk_1.OpenFgaApi({
            apiScheme: process.env.AUTHZ_API_SCHEME ?? "https",
            apiHost: process.env.AUTHZ_API_HOST ?? "",
            storeId: process.env.AUTHZ_STORE_ID,
            credentials: process.env.AUTHZ_BEARER_TOKEN
                ? {
                    method: sdk_1.CredentialsMethod.ApiToken,
                    config: {
                        token: process.env.AUTHZ_BEARER_TOKEN,
                    },
                }
                : { method: sdk_1.CredentialsMethod.None },
        });
    }
    return client;
};
exports.requireClient = requireClient;
const check = async (user, relation, object) => {
    return ((await requireClient().check({
        tuple_key: {
            user,
            relation,
            object,
        },
    })).allowed ?? false);
};
exports.check = check;
/**
 * Writes tuples in a single batch. It silently swallows errors if tuples
 * already exist. This call is idempotent.
 */
const batchWrite = async (tuples) => {
    // OpenFGA API only allows 100 tuples per batch.
    if (tuples.length > 100) {
        for (const chunk of (0, arrays_1.chunks)(tuples, 100)) {
            await batchWrite(chunk);
        }
    }
    else {
        // First we try to write the tuples in a single batch.
        // This fails if one of the tuples already exists.
        // See https://github.com/orgs/openfga/discussions/44
        try {
            await requireClient().write({
                writes: {
                    tuple_keys: tuples,
                },
            });
        }
        catch (e) {
            if (!(0, errors_1.isTupleAlreadyExistsError)(e))
                throw e;
            // Looks like at least one of the tuples already exists, try again one by one
            // We're writing not more than 20 tuples in parallel in order not to
            // overload the API socket.
            for (const chunk of (0, arrays_1.chunks)(tuples, 20)) {
                await Promise.all(chunk.map(async (tuple) => {
                    await write(tuple.user, tuple.relation, tuple.object);
                }));
            }
        }
    }
};
exports.batchWrite = batchWrite;
/**
 * Write a single tuple to the store. It silently swallows errors if the tuple
 * already exists. This call is idempotent.
 */
const write = async (user, relation, object) => {
    try {
        await requireClient().write({
            writes: {
                tuple_keys: [
                    {
                        user,
                        relation,
                        object,
                    },
                ],
            },
        });
    }
    catch (e) {
        if (!(0, errors_1.isTupleAlreadyExistsError)(e))
            throw e;
    }
};
exports.write = write;
const writeUniqueByObject = async (user, relation, object, skipIfNotEmpty = false) => {
    const { tuples } = await requireClient().read({
        tuple_key: {
            relation,
            object,
        },
    });
    const existingTuples = tuples
        ?.map((tuple) => tuple.key)
        ?.filterTruthy()
        ?.undefinedIfEmpty();
    if (existingTuples?.find((tuple) => tuple.user === user)) {
        return;
    }
    if (existingTuples?.length && skipIfNotEmpty) {
        return;
    }
    await requireClient().write({
        deletes: existingTuples && {
            tuple_keys: existingTuples,
        },
        writes: {
            tuple_keys: [
                {
                    user,
                    relation,
                    object,
                },
            ],
        },
    });
};
exports.writeUniqueByObject = writeUniqueByObject;
const writeUniqueByUser = async (user, relation, object, skipIfNotEmpty = false) => {
    const { tuples } = await requireClient().read({
        tuple_key: {
            user,
            relation,
            object: `${object.split(":")[0]}:`,
        },
    });
    const existingTuples = tuples
        ?.map((tuple) => tuple.key)
        ?.filterTruthy()
        ?.undefinedIfEmpty();
    if (existingTuples?.find((tuple) => tuple.object === object)) {
        return;
    }
    if (existingTuples?.length && skipIfNotEmpty) {
        return;
    }
    await requireClient().write({
        deletes: existingTuples && {
            tuple_keys: existingTuples,
        },
        writes: {
            tuple_keys: [
                {
                    user,
                    relation,
                    object,
                },
            ],
        },
    });
};
exports.writeUniqueByUser = writeUniqueByUser;
const readObjects = async (user, relation, object) => {
    const { tuples } = await requireClient().read({
        tuple_key: {
            user,
            relation,
            object,
        },
    });
    return (tuples
        ?.map((tuple) => tuple.key?.object)
        ?.filterTruthy()
        ?.map((object) => object.split(":")[1]) ?? []);
};
exports.readObjects = readObjects;
const readUsers = async (relation, object) => {
    const { tuples } = await requireClient().read({
        tuple_key: {
            relation,
            object,
        },
    });
    return (tuples
        ?.map((tuple) => tuple.key?.user)
        ?.filterTruthy()
        ?.map((user) => user.split(":")[1]) ?? []);
};
exports.readUsers = readUsers;
const listObjects = async (user, relation, type) => {
    const { objects } = await requireClient().listObjects({
        user,
        relation,
        type,
    });
    return objects?.map((object) => object.replace(`${type}:`, "")) ?? [];
};
exports.listObjects = listObjects;
const remove = async (user, relation, object) => {
    try {
        await requireClient().write({
            deletes: {
                tuple_keys: [
                    {
                        user,
                        relation,
                        object,
                    },
                ],
            },
        });
    }
    catch (e) {
        if (!(0, errors_1.isTupleDoesNotExistError)(e))
            throw e;
    }
};
exports.remove = remove;
//# sourceMappingURL=openfga-client.js.map